// Guardar la edición al historial de imágenes generadas — FRONT-ONLY, sin créditos.
// Mismo patrón que EditarVideoIAHistorial.handleSave: sube el PNG a `generadas`
// (full/ + un thumbnail en opt/) e inserta la fila en `imagenes_generadas`
// heredando el `variante_id` de la imagen origen (si no, no aparece en el
// historial filtrado por marca, que usa variantes!inner).

import { supabase } from '../../../lib/supabase';
import type { EditorSource } from '../EditorShell';
import type { Scene } from '../scene/types';
import { compositeScene } from './composite';

const GENERADAS_BUCKET = 'generadas';
const THUMB_LONG_SIDE = 400;

function loadBlobImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('thumb load fail'));
    };
    img.src = url;
  });
}

async function makeThumbnail(blob: Blob): Promise<Blob | null> {
  try {
    const img = await loadBlobImage(blob);
    const r = (img.naturalWidth || 1) / (img.naturalHeight || 1);
    let w: number, h: number;
    if (r >= 1) {
      w = THUMB_LONG_SIDE;
      h = Math.round(THUMB_LONG_SIDE / r);
    } else {
      h = THUMB_LONG_SIDE;
      w = Math.round(THUMB_LONG_SIDE * r);
    }
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise<Blob | null>((res) => c.toBlob(res, 'image/png'));
  } catch {
    return null;
  }
}

/**
 * Compone la escena, sube el PNG al historial y crea la fila. Devuelve el id de
 * la nueva fila (para que el caller pueda preseleccionarla al volver). Lanza si
 * falla el upload/insert (el caller muestra el error).
 */
export async function saveSceneToHistory(scene: Scene, source: EditorSource): Promise<string> {
  const blob = await compositeScene(scene);
  const fileName = `${crypto.randomUUID()}.png`;
  const fullPath = `${source.companyId}/full/${fileName}`;

  const { error: upErr } = await supabase.storage
    .from(GENERADAS_BUCKET)
    .upload(fullPath, blob, { contentType: 'image/png', upsert: false });
  if (upErr) throw new Error(`No se pudo subir la imagen: ${upErr.message}`);

  // Thumbnail (opt/) — best-effort: el historial cae a full/ si no está.
  const optPath = `${source.companyId}/opt/${fileName}`;
  const thumb = await makeThumbnail(blob);
  if (thumb) {
    await supabase.storage
      .from(GENERADAS_BUCKET)
      .upload(optPath, thumb, { contentType: 'image/png', upsert: false })
      .catch(() => {});
  }

  // Quién la guardó: sin esto la fila quedaba sin autor y, en una empresa con equipo,
  // "Generada por" no tenía a quién nombrar. Best-effort: sin sesión queda en null.
  const {
    data: { user: yo },
  } = await supabase.auth.getUser();
  const { data: inserted, error: insErr } = await supabase
    .from('imagenes_generadas')
    .insert({
      id_company: source.companyId,
      user_id: yo?.id ?? null,
      file_name: fileName,
      variante_id: source.varianteId, // heredado de la imagen origen
      // Marca activa; el trigger prefiere la del producto si `variante_id` tiene una
      // (ADR-2026-09-13-brand-id-en-imagenes-generadas).
      brand_id: source.brandId ?? null,
      formato: source.formato,
      active: true,
      modo_utilizado: 'edicion_grafica',
    })
    .select('id')
    .single();
  if (insErr || !inserted) {
    // Cleanup best-effort si el insert falla: sacamos full/ Y el thumbnail opt/
    // (si no, quedaría un objeto huérfano sin fila que lo referencie).
    await supabase.storage.from(GENERADAS_BUCKET).remove([fullPath, optPath]).catch(() => {});
    throw new Error(`No se pudo guardar en el historial: ${insErr?.message ?? 'sin id'}`);
  }
  return (inserted as { id: string }).id;
}
